import { useEffect, useRef, useState } from "react";
import { useAppContext } from "../context/AppContext";
import sellerService from "../services/sellerService";

interface EditStorePoliciesCompProps {
    show: boolean;
    onClose: () => void;
}

interface StorePoliciesFormErrors {
    shippingPolicy?: string;
    returnPolicy?: string;
}

const EditStorePoliciesComp: React.FC<EditStorePoliciesCompProps> = ({
    show,
    onClose,
}) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);

    const { seller, setSeller } = useAppContext();
    const [shippingPolicy, setShippingPolicy] = useState(seller!.shippingPolicy);
    const [returnPolicy, setReturnPolicy] = useState(seller!.returnPolicy);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [storePoliciesFormErrors, setStorePoliciesFormErrors] = useState<StorePoliciesFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }
        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setShippingPolicy(seller!.shippingPolicy);
            setReturnPolicy(seller!.returnPolicy);
            setServiceStatus({ type: '', message: '' });
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    

    const handleStorePoliciesSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setServiceStatus({ type: "", message: "" });

        setIsSubmitting(true);

        try {
            const [updateShipping, updateReturn] = await Promise.all([
                sellerService.updateSellerShippingPolicy(shippingPolicy),
                sellerService.updateSellerReturnPolicy(returnPolicy)
            ]);

            let success = true;

            if (updateShipping === "Success") {
                setSeller(prev => ({ ...prev!, shippingPolicy }));
            } else {
                success = false;
                setServiceStatus({ type: "error", message: "Failed to update shipping policy. Please try again." });
            }

            if (updateReturn === "Success") {
                setSeller(prev => ({ ...prev!, returnPolicy }));
            } else {
                success = false;
                setServiceStatus({ type: "error", message: "Failed to update return policy. Please try again." });
            }

            if (success) {
                onClose();
            }
        } catch (error) {
            setServiceStatus({ type: "error", message: "Failed to update store policies. Please try again." });
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog modal-lg">
                <div className="modal-content rounded-0">
                    <form onSubmit={handleStorePoliciesSubmit}>
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Store Policies</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => bsModalRef.current?.hide()}
                                disabled={isSubmitting}
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label className="form-label">Shipping</label>
                                    <textarea
                                        className={`form-control ${storePoliciesFormErrors.shippingPolicy ? 'is-invalid' : ''}`}
                                        rows={6}
                                        value={shippingPolicy}
                                        onChange={(e) => setShippingPolicy(e.target.value)}
                                        disabled={isSubmitting}
                                    ></textarea>
                                    {storePoliciesFormErrors.shippingPolicy && <div className="invalid-feedback">{storePoliciesFormErrors.shippingPolicy}</div>}
                                </div>
                                <div className="col-md-12">
                                    <label className="form-label">Return</label>
                                    <textarea
                                        className={`form-control ${storePoliciesFormErrors.returnPolicy ? 'is-invalid' : ''}`}
                                        rows={6}
                                        value={returnPolicy}
                                        onChange={(e) => setReturnPolicy(e.target.value)}
                                        disabled={isSubmitting}
                                    ></textarea>
                                    {storePoliciesFormErrors.returnPolicy && <div className="invalid-feedback">{storePoliciesFormErrors.returnPolicy}</div>}
                                </div>
                            </div>

                            {serviceStatus.type.toLowerCase() === "error" && (
                                <div className="col-md-12 mt-2">
                                    <div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div>
                                </div>
                            )}
                            {serviceStatus.type.toLowerCase() === "success" && (
                                <div className="col-md-12 mt-2">
                                    <div className="alert alert-success p-2 mb-0">{serviceStatus.message}</div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button
                                type="button"
                                className="btn btn-outline-dark rounded-0"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-dark rounded-0"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...
                                    </>
                                ) : (
                                    "Save"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditStorePoliciesComp;
